import { useMemo } from 'react';
import dayjs from 'dayjs';
import useGraphqlQuery from '@elementary/common-ui/hooks/useGraphqlQuery';
import useSelectedEnv from '@elementary/common-ui/hooks/useSelectedEnv';
import { ModelRun } from '@elementary/common-ui/types';
import { MODEL_EXEC_HISTORY } from '@elementary/common-ui/constants/queryKeys';
import { DEFAULT_DAYS_BACK } from '@elementary/common-ui/constants';
import {
  ModelRunsExpandedRow as SharedModelRunsExpandedRow,
  ModelRunsExpandedRowProps,
} from '@elementary/common-ui/pages';
import { getModelExecutionHistoryQuery } from '../api';
import { ModelExecutionHistoryResponse } from '../types';
import { toModelRunStatus } from '../utils';

const mapExecutionHistoryToModelRuns = (
  executions: ModelExecutionHistoryResponse['tableAsset']['executionHistory'],
  materialization?: string,
): ModelRun[] =>
  executions.map((execution) => ({
    id: execution.id,
    time_utc: execution.executionTimestamp || '',
    status: toModelRunStatus(execution.status),
    execution_time: execution.executionTime,
    materialization: materialization || '',
    full_refresh: execution.isFullRebuild ?? false,
  }));

type Props = Pick<
  ModelRunsExpandedRowProps,
  'rowData' | 'onOpenAiChat' | 'aiOptimizationEnabled'
>;

const ModelRunsExpandedRow = ({
  rowData,
  onOpenAiChat,
  aiOptimizationEnabled,
}: Props) => {
  const selectedEnvId = useSelectedEnv();

  const { startTime, endTime } = useMemo(
    () => ({
      startTime: dayjs().subtract(DEFAULT_DAYS_BACK, 'day').toISOString(),
      endTime: dayjs().toISOString(),
    }),
    [],
  );

  const { data: executionHistoryData, isFetching: isExecutionHistoryFetching } =
    useGraphqlQuery<ModelExecutionHistoryResponse>({
      query: getModelExecutionHistoryQuery,
      queryKey: [MODEL_EXEC_HISTORY, selectedEnvId, rowData.unique_id],
      variables: {
        id: rowData.unique_id,
        startTime,
        endTime,
      },
      envId: selectedEnvId,
    });

  const chartData = useMemo(
    () =>
      mapExecutionHistoryToModelRuns(
        executionHistoryData?.tableAsset.executionHistory || [],
        rowData.asset_metadata?.materialization,
      ),
    [
      executionHistoryData?.tableAsset.executionHistory,
      rowData.asset_metadata?.materialization,
    ],
  );

  return (
    <SharedModelRunsExpandedRow
      rowData={rowData}
      chartData={chartData}
      isFetching={isExecutionHistoryFetching}
      onOpenAiChat={onOpenAiChat}
      aiOptimizationEnabled={aiOptimizationEnabled}
    />
  );
};

export default ModelRunsExpandedRow;
